const content = `
# Peak Optimization Percentage (PoP)

**PoP** measures how effectively a multi-asset site manages its peak grid demand compared to the theoretical optimum. It is the primary quality metric for both orchestration algorithms and load forecasting.

## Definition

\`\`\`
PoP = (1 - (Orchestrated Peak - Global Optimum) / (Baseline Peak - Global Optimum)) x 100%
\`\`\`

Where:
- **Baseline Peak** -- Peak demand without any optimization (all assets operate independently)
- **Orchestrated Peak** -- Peak demand after coordination (battery, EV, HVAC respond to price signals)
- **Global Optimum** -- Theoretical minimum peak achievable with perfect foresight and centralized control

## Interpretation

| PoP | Meaning |
|-----|---------|
| 100% | Achieved the global optimum -- impossible gap |
| 90%+ | Excellent -- within 10% of theoretical best |
| 80-90% | Good -- acceptable for production deployment |
| < 80% | Needs improvement -- check forecasting or coordination quality |

## Why PoP Matters

Peak demand drives **demand charges**, which often represent 30-50% of a commercial electricity bill. A site with 500 kW baseline peak and 90% PoP might reduce peak to 360 kW, saving significant demand charges versus uncoordinated operation (450 kW peak, 50% PoP).

## Siloed vs Orchestrated Peaks

**Siloed operation**: Each asset (battery, EV charger, COLD system) optimizes independently. The battery shaves its local peak, but doesn't know the EV chargers are about to spike. Result: assets work at cross-purposes, higher aggregate peak.

**Orchestrated operation**: A coordinator (ADMM or column generation) sends price signals or power targets to all assets. Each agent adjusts its schedule to minimize the shared peak. Result: coordinated load shifting produces a flat demand profile.

## PoP in Forecasting Context

Forecasting quality directly affects PoP because the optimizer plans based on predicted load:

- **Perfect foresight** (Scenario A): Optimizer sees true future load. Achieves highest PoP.
- **Good forecast** (MAPE < 15%): Minor mismatch causes slight peak overshoot. PoP > 90%.
- **Poor forecast** (MAPE > 30%): Optimizer plans for wrong peaks, actual peak is unmanaged. PoP drops.
- **Cold-start forecast**: Using only building metadata, target is PoP > 85%.

## Measuring PoP

In validation, PoP is measured by:

1. Running a perfect-foresight optimization to get the global optimum peak
2. Running the same optimization with the candidate forecast
3. Computing orchestrated peak from the forecast-based schedule applied to actual load
4. Applying the PoP formula

This is done for each test building, and the median PoP across all test buildings is reported.
`;

export default content;
